import type { TFunction } from 'i18next';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { ClickableRow } from '@/components/list/ClickableRow';
import { Button } from '@/components/ui/button';
import { categoryLabel, DocumentBadge, formatShortDate } from '../list/expense-labels';
import type { DocumentStatus } from '../list/expense-labels';
import { allowanceLine, tripLine } from '../list/row-title';
import { isAllowanceLine } from '../flat-rate/category';
import { formatAmount } from '../money';
import { RowDecisions, RowNote, type OnRowDecision } from './RowDecisions';
import { RowClass, RowClassHint, rowsClassified } from './RowClass';
import type { ReportExpenseRow } from './types';

type Props = {
  readonly rows: readonly ReportExpenseRow[];
  readonly editable: boolean;
  /** Decision 203: Gestione or an approver takes a flat-rate line out; its owner never does. */
  readonly removesAllowance?: boolean;
  readonly busy: boolean;
  readonly onRemove: (expenseId: string) => void;
  /** Set on the decision page: the approver's per-row actions. */
  readonly onDecide?: OnRowDecision;
};

function nameOf(row: ReportExpenseRow, t: TFunction<'expenses'>): string {
  return (
    allowanceLine(row, t) ||
    row.supplierName ||
    tripLine(row) ||
    row.description ||
    t('reports.page.noSupplier')
  );
}

/**
 * «Togli» on a row: an ordinary expense while the note is editable and not rejected; a flat-rate
 * line for Gestione or an approver (decision 203) unless an approver rejected it on its own
 * (`rejection`). A refused report marks every line `rejected`, but those lines still go.
 */
function removable(row: ReportExpenseRow, editable: boolean, removesAllowance: boolean): boolean {
  if (isAllowanceLine(row)) return removesAllowance && row.rejection == null;
  return editable && !row.rejected;
}

function documentOf(row: ReportExpenseRow) {
  return (
    <DocumentBadge
      status={(row.documentStatus || 'attached') as DocumentStatus}
      missing={row.documentMissing}
    />
  );
}

/** The report's expenses: a table from 620 px of shell width (spec §1.3), cards below. */
export function ReportExpenses(props: Props) {
  return (
    <>
      <RowClassHint rows={props.rows} />
      <ExpensesTable {...props} />
      <ExpenseCards {...props} />
    </>
  );
}

function TableHead({ classified }: { readonly classified: boolean }) {
  const { t } = useTranslation('expenses');
  const { t: tp } = useTranslation('expenses-parity');
  const th = 'px-3 py-2.5 font-semibold';
  return (
    <thead className="bg-muted/50 text-muted-foreground text-left text-xs tracking-wide uppercase">
      <tr>
        <th scope="col" className={th}>
          {t('list.columns.date')}
        </th>
        <th scope="col" className={th}>
          {t('list.columns.supplier')}
        </th>
        <th scope="col" className={th}>
          {t('list.columns.category')}
        </th>
        <th scope="col" className={`${th} text-right`}>
          {t('list.columns.total')}
        </th>
        <th scope="col" className={th}>
          {t('reports.page.reimbursable')}
        </th>
        <th scope="col" className={th}>
          {t('reports.page.document')}
        </th>
        {classified ? (
          <th scope="col" className={th}>
            {tp('class.columnLong')}
          </th>
        ) : null}
        <th scope="col" className={th}>
          <span className="sr-only">{t('reports.page.actions')}</span>
        </th>
      </tr>
    </thead>
  );
}

function ExpensesTable({
  rows,
  editable,
  removesAllowance = false,
  busy,
  onRemove,
  onDecide,
}: Props) {
  const { t, i18n } = useTranslation('expenses');
  const navigate = useNavigate();
  const classified = rowsClassified(rows);
  return (
    <div className="border-border hidden overflow-x-auto rounded-xl border @min-[620px]/shell:block">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{t('reports.page.expenses')}</caption>
        <TableHead classified={classified} />
        <tbody>
          {rows.map((row) => (
            <ClickableRow
              key={row.id}
              className="border-border border-t"
              onOpen={() => void navigate(`/expenses/${encodeURIComponent(row.id)}`)}
            >
              <td className="px-3 py-3 tabular-nums">{formatShortDate(row.date)}</td>
              <td className="px-3 py-3">
                {/* A real link for keyboard and screen readers; the row click is a mouse convenience. */}
                <Link
                  to={`/expenses/${encodeURIComponent(row.id)}`}
                  className="text-foreground font-semibold hover:underline"
                >
                  {nameOf(row, t)}
                </Link>
                <RowNote row={row} />
              </td>
              <td className="px-3 py-3">{categoryLabel(row.category, i18n.resolvedLanguage)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{formatAmount(row.total)}</td>
              <td className="px-3 py-3">
                <ReimbursableCell row={row} />
              </td>
              <td className="px-3 py-3">{documentOf(row)}</td>
              {classified ? (
                <td className="px-3 py-3">
                  <RowClass row={row} name={nameOf(row, t)} />
                </td>
              ) : null}
              <td className="px-3 py-3 text-right whitespace-nowrap">
                {removable(row, editable, removesAllowance) ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => onRemove(row.id)}
                  >
                    {t('reports.page.remove')}
                  </Button>
                ) : null}
                {onDecide ? (
                  <RowDecisions row={row} name={nameOf(row, t)} busy={busy} onDecide={onDecide} />
                ) : null}
              </td>
            </ClickableRow>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExpenseCards({
  rows,
  editable,
  removesAllowance = false,
  busy,
  onRemove,
  onDecide,
}: Props) {
  const { t, i18n } = useTranslation('expenses');
  return (
    <ul
      aria-label={t('reports.page.expenses')}
      className="flex flex-col gap-2 @min-[620px]/shell:hidden"
    >
      {rows.map((row) => (
        <li
          key={row.id}
          className="border-border bg-card grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 rounded-xl border p-3"
        >
          <Link to={`/expenses/${encodeURIComponent(row.id)}`} className="truncate font-semibold">
            {nameOf(row, t)}
          </Link>
          <b className="tabular-nums">{formatAmount(row.total)}</b>
          <small className="text-muted-foreground">
            {categoryLabel(row.category, i18n.resolvedLanguage)}
            {/* An allowance line's name already carries its day. */}
            {isAllowanceLine(row) ? null : ` · ${formatShortDate(row.date)}`}
          </small>
          <span className="flex items-center justify-end gap-1">
            {removable(row, editable, removesAllowance) ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-11 min-w-11"
                disabled={busy}
                onClick={() => onRemove(row.id)}
              >
                {t('reports.page.remove')}
              </Button>
            ) : null}
            {onDecide ? (
              <RowDecisions
                row={row}
                name={nameOf(row, t)}
                busy={busy}
                onDecide={onDecide}
                compact
              />
            ) : null}
          </span>
          <span className="col-span-2 flex flex-col gap-1 empty:hidden">
            {row.rejected ? (
              <small className="text-destructive">{t('reports.page.rejected')}</small>
            ) : null}
            {row.documentMissing ? documentOf(row) : null}
            <RowClass row={row} name={nameOf(row, t)} />
            <RowNote row={row} />
            {!row.reimbursable && !row.rejected && row.documentMissing ? (
              <small className="text-destructive">{t('reports.page.decision.lateDocument')}</small>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ReimbursableCell({ row }: { readonly row: ReportExpenseRow }) {
  const { t } = useTranslation('expenses');
  if (row.rejected) return <span className="text-destructive">{t('reports.page.rejected')}</span>;
  if (!row.reimbursable && row.documentMissing) {
    return <span className="text-destructive">✕ {t('reports.page.decision.lateDocument')}</span>;
  }
  if (row.reimbursable) {
    return <Check size={15} aria-label={t('reports.page.yes')} className="text-success" />;
  }
  return <span className="text-muted-foreground">{t('reports.page.no')}</span>;
}
